import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { reportReasonsAPI, type ReportReason, type CreateReportReasonRequest, type UpdateReportReasonRequest, type ReportEntityType } from "../api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Edit, Trash2, GripVertical, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

// Entity types with labels
const ENTITY_TYPES: { value: ReportEntityType; label: string }[] = [
  { value: 'user', label: 'User' },
  { value: 'message', label: 'Message' },
  { value: 'event', label: 'Event' },
  { value: 'idea', label: 'Idea' },
  { value: 'space', label: 'Space' },
];

export function ReportReasonsPage() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingReason, setEditingReason] = useState<ReportReason | null>(null);
  const [reasonToDelete, setReasonToDelete] = useState<ReportReason | null>(null);
  const [includeInactive, setIncludeInactive] = useState(true);

  const [formData, setFormData] = useState<CreateReportReasonRequest>({
    key: "",
    displayName: "",
    types: ['user', 'event', 'space', 'idea', 'message'], // All types by default
    order: 0,
    isActive: true,
  });

  const queryClient = useQueryClient();

  // Fetch report reasons
  const { data: reportReasonsData, isLoading, error } = useQuery({
    queryKey: ['reportReasons', includeInactive],
    queryFn: () => reportReasonsAPI.getAllReportReasons(includeInactive),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateReportReasonRequest) => reportReasonsAPI.createReportReason(data),
    onSuccess: () => {
      toast.success("Report reason created successfully");
      queryClient.invalidateQueries({ queryKey: ['reportReasons'] });
      handleCloseCreateDialog();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to create report reason");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReportReasonRequest }) =>
      reportReasonsAPI.updateReportReason(id, data),
    onSuccess: () => {
      toast.success("Report reason updated successfully");
      queryClient.invalidateQueries({ queryKey: ['reportReasons'] });
      handleCloseEditDialog();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to update report reason");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => reportReasonsAPI.deleteReportReason(id, false),
    onSuccess: () => {
      toast.success("Report reason deactivated successfully");
      queryClient.invalidateQueries({ queryKey: ['reportReasons'] });
      handleCloseDeleteDialog();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to deactivate report reason");
    },
  });

  const reportReasons = reportReasonsData?.reasons || [];

  console.log('=== DEBUG INFO ===');
  console.log('isLoading:', isLoading);
  console.log('error:', error);
  console.log('reportReasonsData:', reportReasonsData);
  console.log('reportReasons:', reportReasons);
  console.log('reportReasons.length:', reportReasons.length);
  console.log('==================');

  const handleOpenCreateDialog = () => {
    setFormData({
      key: "",
      displayName: "",
      types: ['user', 'event', 'space', 'idea', 'message'],
      order: reportReasons.length,
      isActive: true,
    });
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setFormData({
      key: "",
      displayName: "",
      types: ['user', 'event', 'space', 'idea', 'message'],
      order: 0,
      isActive: true,
    });
  };

  const handleOpenEditDialog = (reason: ReportReason) => {
    setEditingReason(reason);
    setFormData({
      key: reason.key,
      displayName: reason.displayName,
      types: reason.types || ['user', 'event', 'space', 'idea', 'message'],
      order: reason.order,
      isActive: reason.isActive,
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingReason(null);
    setFormData({
      key: "",
      displayName: "",
      types: ['user', 'event', 'space', 'idea', 'message'],
      order: 0,
      isActive: true,
    });
  };

  const handleOpenDeleteDialog = (reason: ReportReason) => {
    setReasonToDelete(reason);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setReasonToDelete(null);
  };

  const handleCreateSubmit = () => {
    if (!formData.key || !formData.displayName) {
      toast.error("Key and Display Name are required");
      return;
    }
    createMutation.mutate(formData);
  };

  const handleUpdateSubmit = () => {
    if (!editingReason) return;
    if (!formData.displayName) {
      toast.error("Display Name is required");
      return;
    }
    updateMutation.mutate({
      id: editingReason._id,
      data: formData,
    });
  };

  const handleDeleteConfirm = () => {
    if (!reasonToDelete) return;
    deleteMutation.mutate(reasonToDelete._id);
  };

  const handleToggleEntityType = (type: ReportEntityType) => {
    setFormData(prev => {
      const currentTypes = prev.types || [];
      const hasType = currentTypes.includes(type);

      if (hasType) {
        // Remove the type
        return { ...prev, types: currentTypes.filter(t => t !== type) };
      } else {
        // Add the type
        return { ...prev, types: [...currentTypes, type] };
      }
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingState message="Loading report reasons..." />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <ErrorState message="Failed to load report reasons" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Report Reasons"
          description="Manage report reasons for content moderation across the platform"
        />

        {/* Actions Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch
              id="include-inactive"
              checked={includeInactive}
              onCheckedChange={setIncludeInactive}
            />
            <Label htmlFor="include-inactive">Show inactive reasons</Label>
          </div>
          <Button onClick={handleOpenCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Create Report Reason
          </Button>
        </div>

        {/* Report Reasons Table */}
        <Card>
          <CardHeader>
            <CardTitle>Report Reasons ({reportReasons.length})</CardTitle>
            <CardDescription>
              Drag and drop to reorder. Active reasons are shown to users when reporting content.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {reportReasons.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No report reasons found. Data: {JSON.stringify(reportReasonsData)}
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Key</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Applies To</TableHead>
                      <TableHead className="text-center">Order</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportReasons.map((reason) => (
                      <TableRow key={reason._id}>
                        <TableCell className="w-12">
                          <GripVertical className="h-5 w-5 text-muted-foreground" />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{reason.key}</TableCell>
                        <TableCell className="font-medium">{reason.displayName}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {reason.types && reason.types.length > 0 ? (
                              reason.types.map((type) => (
                                <Badge key={type} variant="outline" className="text-xs">
                                  {type}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground">All</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{reason.order}</TableCell>
                        <TableCell>
                          <Badge variant={reason.isActive ? "default" : "secondary"}>
                            {reason.isActive ? (
                              <>
                                <Eye className="h-3 w-3 mr-1" />
                                Active
                              </>
                            ) : (
                              <>
                                <EyeOff className="h-3 w-3 mr-1" />
                                Inactive
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleOpenEditDialog(reason)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => handleOpenDeleteDialog(reason)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Report Reason</DialogTitle>
              <DialogDescription>
                Add a new report reason for users to select when reporting content
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="key">Key *</Label>
                <Input
                  id="key"
                  placeholder="e.g., harassment_threats"
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                />
                <p className="text-xs text-muted-foreground">
                  Unique identifier (lowercase, underscores only)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name *</Label>
                <Input
                  id="displayName"
                  placeholder="e.g., Harassment/Threats"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Applies to Entity Types *</Label>
                <div className="grid grid-cols-2 gap-3">
                  {ENTITY_TYPES.map((entityType) => (
                    <div key={entityType.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`create-type-${entityType.value}`}
                        checked={formData.types?.includes(entityType.value)}
                        onCheckedChange={() => handleToggleEntityType(entityType.value)}
                      />
                      <Label htmlFor={`create-type-${entityType.value}`} className="font-normal cursor-pointer">
                        {entityType.label}
                      </Label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Select which entity types this reason can be used for
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="order">Order</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
                <p className="text-xs text-muted-foreground">
                  Lower numbers appear first
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseCreateDialog}>
                Cancel
              </Button>
              <Button onClick={handleCreateSubmit} disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Report Reason</DialogTitle>
              <DialogDescription>
                Update the report reason details
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-key">Key</Label>
                <Input
                  id="edit-key"
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-displayName">Display Name *</Label>
                <Input
                  id="edit-displayName"
                  placeholder="e.g., Harassment/Threats"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Applies to Entity Types *</Label>
                <div className="grid grid-cols-2 gap-3">
                  {ENTITY_TYPES.map((entityType) => (
                    <div key={entityType.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-type-${entityType.value}`}
                        checked={formData.types?.includes(entityType.value)}
                        onCheckedChange={() => handleToggleEntityType(entityType.value)}
                      />
                      <Label htmlFor={`edit-type-${entityType.value}`} className="font-normal cursor-pointer">
                        {entityType.label}
                      </Label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Select which entity types this reason can be used for
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-order">Order</Label>
                <Input
                  id="edit-order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label htmlFor="edit-isActive">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseEditDialog}>
                Cancel
              </Button>
              <Button onClick={handleUpdateSubmit} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Updating..." : "Update"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Deactivate Report Reason</DialogTitle>
              <DialogDescription>
                Are you sure you want to deactivate "{reasonToDelete?.displayName}"? This will hide it from users but preserve historical data.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDeleteDialog}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deactivating..." : "Deactivate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
